/* eslint-disable require-await */
import { type Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import moment from 'moment';
import jwtDecode from 'jwt-decode';
import axios from 'axios';
import { toastConfig } from 'app/utils/constants';
import { type BateriaEntity } from 'app/interfaces/vigilancia/bateryEntity.interface';
import { type AppState } from 'app/App.store';
import { sendTagLog } from '@ui-core/utils/sendTagLog.lib';
import { type AchsJWT } from '@ui-core/interfaces/AchsJWTInterface';
import AxiosClient from '@ui-core/config/axiosClient';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

const token = localStorage.getItem('Authorization');
let azureObjectId: string | undefined;
if (token) {
	const tokenDecoded: AchsJWT = jwtDecode(token);
	azureObjectId = tokenDecoded.azureObjectId;
}

export interface ChangeStateResponseEntity {
	estado: number;
	descripcion: string;
	folio: string;
}
export interface BAPIRET2Entity {
	item: BateriaEntity[];
}

export interface BateriaResponseEntity {
	FOLIO_CERT: string;
	BATERIA_DESC: string;
	FECHA_VENCIMIENTO?: string;
	ZNCIELCONC_DESC?: string;
}

export interface CertificadoResponseEntity {
	EMPRESA: number;
	RUT_PACIENTE: string;
	NOMBRES: string;
	APELLIDO1: string;
	APELLIDO2: string;
	DOKNR: string;
	DOKVR: string;
	ZNSEPTIEVA: string;
	FECHA_PRESENTA: string;
	CENTRO_ASIST: string;
	ZNSEPAENRE: number;
	CHECK_CERT: string;
	RUT_EMPRESA: string;
	NOMBRE_EMPRESA: string;
	BATERIAS: BateriaResponseEntity[];
}

export interface ITCERTSEPEntity {
	item: CertificadoResponseEntity[];
}

export interface SelCertificadosRespose {
	BAPIRET2: BAPIRET2Entity;
	IT_CERTSEP: ITCERTSEPEntity;
	TOTAL: number;
}

export interface SelPdfResponse {
	'ZISHRFC_BIN_PRINT_CERTSEP.Response': {
		BAPIRET2: {
			item: BateriaEntity;
		};
		BIN_FILE: string;
	};
}

export interface SelState {
	loadingSel: boolean;
	responseDataSel: SelCertificadosRespose;
	errorSel: boolean;
	errorMsgSel: string;
	loadingPdfSel: boolean;
	loadingPdfDownloadSel: boolean;
	responseDataPdfSel: SelPdfResponse;
	errorPdfSel: boolean;
	errorMsgPdfSel: string;
	pageSel: number;
	totalSel: number;
	loadingChangeState: boolean;
	responseChangeState: ChangeStateResponseEntity;
	errorChangeState: boolean;
	errorMsgChangeState: string;
}

export const initialState: SelState = {
	loadingSel: false,
	responseDataSel: {} as SelCertificadosRespose,
	errorSel: false,
	errorMsgSel: '',
	loadingPdfSel: false,
	loadingPdfDownloadSel: false,
	responseDataPdfSel: {} as SelPdfResponse,
	errorPdfSel: false,
	errorMsgPdfSel: '',
	pageSel: 1,
	totalSel: 1,
	loadingChangeState: false,
	responseChangeState: {} as ChangeStateResponseEntity,
	errorChangeState: false,
	errorMsgChangeState: '',
};

export const selSlice = createSlice({
	name: 'sel',
	initialState,
	reducers: {
		loadingSel: (state) => {
			state.loadingSel = false;
			state.responseDataSel = {} as SelCertificadosRespose;
			state.errorSel = false;
			state.errorMsgSel = '';
		},
		successSel: (state, action: PayloadAction<SelCertificadosRespose>) => {
			state.loadingSel = true;
			state.responseDataSel = action.payload;
			state.errorSel = false;
			state.errorMsgSel = '';
		},
		errorSel: (state, action: PayloadAction<string>) => {
			state.loadingSel = false;
			state.responseDataSel = {} as SelCertificadosRespose;
			state.errorSel = true;
			state.errorMsgSel = action.payload;
		},
		loadingPdfSel: (state) => {
			state.loadingPdfSel = true;
			state.responseDataPdfSel = {} as SelPdfResponse;
			state.errorPdfSel = false;
			state.errorMsgPdfSel = '';
			state.loadingPdfDownloadSel = false;
		},
		successPdfSel: (state, action: PayloadAction<SelPdfResponse>) => {
			state.loadingPdfSel = false;
			state.responseDataPdfSel = action.payload;
			state.errorPdfSel = false;
			state.errorMsgPdfSel = '';
			state.loadingPdfDownloadSel = false;
		},
		loadingPdfDownloadSel: (state) => {
			state.loadingPdfDownloadSel = true;
		},
		successPdfDownloadSel: (state) => {
			state.loadingPdfDownloadSel = false;
		},
		errorPdfSel: (state, action: PayloadAction<string>) => {
			state.loadingPdfSel = false;
			state.responseDataPdfSel = {} as SelPdfResponse;
			state.errorPdfSel = true;
			state.errorMsgPdfSel = action.payload;
			state.loadingPdfDownloadSel = false;
		},
		setPageSel: (state, action: PayloadAction<number>) => {
			state.pageSel = action.payload;
		},
		setTotalSel: (state, action: PayloadAction<number>) => {
			state.totalSel = action.payload;
		},
		loadingChangeState: (state) => {
			state.loadingChangeState = true;
			state.responseChangeState = {} as ChangeStateResponseEntity;
			state.errorChangeState = false;
			state.errorMsgChangeState = '';
		},
		successChangeState: (
			state,
			action: PayloadAction<ChangeStateResponseEntity>,
		) => {
			state.loadingChangeState = false;
			state.responseChangeState = action.payload;
			state.errorChangeState = false;
			state.errorMsgChangeState = '';
		},
		errorChangeState: (state, action: PayloadAction<string>) => {
			state.loadingChangeState = false;
			state.responseChangeState = {} as ChangeStateResponseEntity;
			state.errorChangeState = true;
			state.errorMsgChangeState = action.payload;
		},
	},
});

const callTagManagerSEL = (gaResponse: string) => {
	const dataLayer = {
		event: 'gaEvent',
		gaCategory: 'Trabajador',
		gaAction: 'Resultado exámenes de salud',
		gaLabel: 'Clic Btn: Certificado SEL',
		gaResponse,
		gaValue: '',
		gaNonInteraction: false,
		id: azureObjectId,
		timeStamp: moment().format(import.meta.env.REACT_APP_GTM_MOMENT_FORMAT),
	};

	TagManager.dataLayer({
		dataLayer,
		dataLayerName: 'PageDataLayer',
	});

	sendTagLog(dataLayer);
};

export const {
	loadingSel,
	successSel,
	errorSel,
	loadingPdfSel,
	successPdfSel,
	errorPdfSel,
	setPageSel,
	setTotalSel,
	loadingChangeState,
	successChangeState,
	errorChangeState,
	successPdfDownloadSel,
	loadingPdfDownloadSel,
} = selSlice.actions;

export const useSelSelector = () =>
	useSelector<AppState, SelState>(({ [selSlice.name]: slice }) => slice);

export const fetchSelThunk =
	(page: number) =>
	async (dispatch: Dispatch): Promise<void> => {
		const axiosApi = AxiosClient(`${import.meta.env.REACT_APP_API_ACHS}`);
		dispatch(loadingSel());
		try {
			const response = await axiosApi.get(
				`bff-vigilancia/v1/sel/certificados?PAGINA=${page}&CANTIDAD=6`,
			);
			const selData: SelCertificadosRespose = response.data;
			dispatch(successSel(selData));
			dispatch(setTotalSel(selData.TOTAL));
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				dispatch(errorSel(error.response?.data.message));
			}
		}
	};

export const fetchPdfSelThunk =
	(documento: string, download?: boolean, docType?: string) =>
	async (dispatch: Dispatch): Promise<void> => {
		const axiosApi = AxiosClient(`${import.meta.env.REACT_APP_API_ACHS}`);
		dispatch(loadingPdfSel());
		try {
			const response = await axiosApi.get(
				`bff-vigilancia/v1/sel/certificadoPDF?DOCUMENTO=${documento}`,
			);
			const selData: SelPdfResponse = response.data;

			if (download && docType) {
				const linkSource = `data:application/pdf;base64,${selData['ZISHRFC_BIN_PRINT_CERTSEP.Response'].BIN_FILE}`;
				const downloadLink = document.createElement('a');

				const date = new Date().toLocaleDateString('CO-co', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				});
				const fileName = `${docType} ${date.split('/').join('-')}`;

				downloadLink.href = linkSource;
				downloadLink.download = fileName;
				downloadLink.click();
				dispatch(successPdfDownloadSel());
				toast.success('Descarga realizada con éxito', toastConfig);
				callTagManagerSEL('Éxito');
			} else {
				dispatch(successPdfSel(selData));
			}
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				dispatch(errorPdfSel(error.message));
			}
			toast.error(
				'Descarga no realizada. Vuelva a intentarlo',
				toastConfig,
			);
			callTagManagerSEL('Error');
		}
	};

export const setPagesSel =
	(page: number) =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch(setPageSel(page));
	};

export const fetchChangeStateThunk =
	(state: string, folio: string) =>
	async (dispatch: Dispatch): Promise<void> => {
		const axiosApi = AxiosClient(`${import.meta.env.REACT_APP_API_ACHS}`);
		dispatch(loadingChangeState());
		try {
			const response = await axiosApi.put(
				'bff-vigilancia/v1/sel/cambioEstado',
				{ estado_autorizacion: state, folio },
			);
			const changeStateData: ChangeStateResponseEntity = response.data;
			dispatch(successChangeState(changeStateData));
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				dispatch(errorChangeState(error.response?.data.message));
			}
		}
	};

export default selSlice.reducer;
